import { Button, Divider, InputGroup, TextArea, Card } from '@blueprintjs/core';
import { email } from '@axo/shared/data-access/types';
import { classNames } from '../../../utils/classNames';
import { CreatedAtTag, CreatorTag, EmailIcon } from '../CommunicationItem';
import cardClasses from '../CommunicationItem/Card.module.css';
import { SelectQuote } from '../SelectQuote';
import { includesLoanQuoteTemplateKey } from '../templateKey';

interface IEmailCardEdit {
  email: email.CreateEmail;
  to: string;
  setEmail: (email: email.CreateEmail) => void;
  saveEmail: () => void;
  cancelEmail: () => void;
  template?: email.EmailTemplate;
  applicationID: string;
  marketCountry: string;
}

export function EmailCardEdit({
  email,
  to,
  setEmail,
  saveEmail,
  cancelEmail,
  template,
  applicationID,
  marketCountry,
}: IEmailCardEdit) {
  const onValueChange = (name: keyof email.CreateEmail, value: string) => {
    setEmail({
      ...email,
      [name]: value,
    });
  };

  const onLoanQuoteIDChange = (loanQuoteID: string | undefined) => {
    setEmail({
      ...email,
      LoanQuoteID: loanQuoteID,
    });
  };

  const showSelectQuote =
    includesLoanQuoteTemplateKey(email.Body) || !!email.LoanQuoteID;

  return (
    <Card className={cardClasses.card}>
      <div className={cardClasses.topBar}>
        <EmailIcon />
        <CreatorTag creatorID={email.CreatorID} />
        {email.CreatedAt && (
          <CreatedAtTag label="Sent at:" createdAt={email.CreatedAt} />
        )}
        <div className={cardClasses.fill} />
      </div>
      <Divider />
      {showSelectQuote && (
        <>
          <SelectQuote
            applicationID={applicationID}
            marketCountry={marketCountry}
            onChange={onLoanQuoteIDChange}
          />
          <Divider />
        </>
      )}
      <div className={cardClasses.body}>
        <div className={cardClasses.formRow}>
          <span className={cardClasses.label}>To: </span>
          <InputGroup
            small={true}
            value={to}
            className={classNames(cardClasses.input, cardClasses.readOnly)}
            fill
            readOnly
          />
        </div>
        {template && (
          <div className={cardClasses.formRow}>
            <span className={cardClasses.label}>Name:</span>
            <span>{template?.Name}</span>
          </div>
        )}
        {template?.AllowsCustomContent && (
          <div className={cardClasses.formRow}>
            <span className={cardClasses.label}>Subject: </span>
            <InputGroup
              onChange={(e) => onValueChange('Subject', e.target.value)}
              small
              value={email.Subject}
              className={classNames(
                cardClasses.input,
                !template?.AllowsCustomContent && cardClasses.readOnly
              )}
              placeholder="Add an email subject..."
              fill
              readOnly={!template?.AllowsCustomContent}
            />
          </div>
        )}
        {template?.AllowsCustomContent && (
          <TextArea
            small
            onChange={(e) => onValueChange('Body', e.target.value)}
            value={email.Body}
            className={classNames(cardClasses.textArea, cardClasses.input)}
            placeholder="Write your custom email..."
            readOnly={!template?.AllowsCustomContent}
          />
        )}
      </div>
      <Divider />
      <div className={cardClasses.bottomRow}>
        <div className={cardClasses.fill} />
        <Button
          onClick={saveEmail}
          outlined
          minimal
          intent="primary"
          icon="tick"
        >
          Confirm
        </Button>
        <Button
          onClick={cancelEmail}
          minimal
          outlined
          intent="danger"
          icon="cross"
        >
          Cancel
        </Button>
      </div>
    </Card>
  );
}
